/** GeoJSON / GPX / KML serialization for the selected route and its waypoints. */

import { RouteResult, Waypoint } from '../types';

export interface ExportInput {
  name: string;
  waypoints: Waypoint[];
  route: RouteResult | null;
  profile: string;
}

const XML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&apos;',
};

export function escapeXml(value: string): string {
  return String(value).replace(/[&<>"']/g, (c) => XML_ESCAPES[c]);
}

function coord(n: number): string {
  // Six decimals is ~0.1 m — plenty for a planned route and keeps files small.
  return Number(n.toFixed(6)).toString();
}

function waypointName(wp: Waypoint, index: number, total: number): string {
  if (wp.label) return wp.label;
  if (index === 0) return 'Origin';
  if (index === total - 1) return 'Destination';
  return `Stop ${index}`;
}

export function toGeoJSON({ name, waypoints, route, profile }: ExportInput): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = waypoints.map((wp, i) => ({
    type: 'Feature',
    properties: {
      name: waypointName(wp, i, waypoints.length),
      address: wp.address || null,
      sequence: i,
      role: i === 0 ? 'origin' : i === waypoints.length - 1 ? 'destination' : 'via',
    },
    geometry: { type: 'Point', coordinates: [wp.longitude, wp.latitude] },
  }));

  if (route) {
    features.push({
      type: 'Feature',
      properties: {
        name,
        profile,
        distance_meters: route.distance,
        duration_seconds: route.duration,
      },
      geometry: route.geometry,
    });
  }

  return { type: 'FeatureCollection', features };
}

export function toGpx({ name, waypoints, route }: ExportInput): string {
  const wpts = waypoints.map((wp, i) => [
    `  <wpt lat="${coord(wp.latitude)}" lon="${coord(wp.longitude)}">`,
    `    <name>${escapeXml(waypointName(wp, i, waypoints.length))}</name>`,
    wp.address ? `    <desc>${escapeXml(wp.address)}</desc>` : null,
    '  </wpt>',
  ].filter(Boolean).join('\n')).join('\n');

  const trkpts = (route?.geometry.coordinates || [])
    .map((c) => `        <trkpt lat="${coord(c[1])}" lon="${coord(c[0])}" />`)
    .join('\n');

  const track = route
    ? [
      '  <trk>',
      `    <name>${escapeXml(name)}</name>`,
      '    <trkseg>',
      trkpts,
      '    </trkseg>',
      '  </trk>',
    ].join('\n')
    : '';

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<gpx version="1.1" creator="Pathfinder Route Planner" xmlns="http://www.topografix.com/GPX/1/1">',
    '  <metadata>',
    `    <name>${escapeXml(name)}</name>`,
    `    <time>${new Date().toISOString()}</time>`,
    '  </metadata>',
    wpts,
    track,
    '</gpx>',
  ].filter((line) => line !== '').join('\n');
}

export function toKml({ name, waypoints, route }: ExportInput): string {
  const placemarks = waypoints.map((wp, i) => [
    '    <Placemark>',
    `      <name>${escapeXml(waypointName(wp, i, waypoints.length))}</name>`,
    wp.address ? `      <description>${escapeXml(wp.address)}</description>` : null,
    '      <Point>',
    `        <coordinates>${coord(wp.longitude)},${coord(wp.latitude)},0</coordinates>`,
    '      </Point>',
    '    </Placemark>',
  ].filter(Boolean).join('\n')).join('\n');

  const lineCoords = (route?.geometry.coordinates || [])
    .map((c) => `${coord(c[0])},${coord(c[1])},0`)
    .join(' ');

  const lineString = route
    ? [
      '    <Placemark>',
      `      <name>${escapeXml(name)}</name>`,
      '      <styleUrl>#routeLine</styleUrl>',
      '      <LineString>',
      '        <tessellate>1</tessellate>',
      `        <coordinates>${lineCoords}</coordinates>`,
      '      </LineString>',
      '    </Placemark>',
    ].join('\n')
    : '';

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<kml xmlns="http://www.opengis.net/kml/2.2">',
    '  <Document>',
    `    <name>${escapeXml(name)}</name>`,
    '    <Style id="routeLine">',
    '      <LineStyle><color>ff42b9f5</color><width>4</width></LineStyle>',
    '    </Style>',
    placemarks,
    lineString,
    '  </Document>',
    '</kml>',
  ].filter((line) => line !== '').join('\n');
}

export interface ParsedImport {
  coordinates: Array<{ longitude: number; latitude: number; label?: string }>;
  /** Set when the file carried a track/line we can name the route after. */
  name?: string;
}

/** Read waypoints out of a GPX file: explicit <wpt> first, else a sampled track. */
export function parseGpx(xml: string, maxPoints = 25): ParsedImport {
  const doc = new DOMParser().parseFromString(xml, 'text/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('That GPX file could not be parsed.');
  }
  const name = doc.querySelector('metadata > name')?.textContent
    || doc.querySelector('trk > name')?.textContent
    || undefined;

  const read = (nodes: ArrayLike<Element>) => Array.from(nodes).flatMap((node) => {
    const latitude = Number(node.getAttribute('lat'));
    const longitude = Number(node.getAttribute('lon'));
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];
    return [{
      latitude,
      longitude,
      label: node.getElementsByTagName('name')[0]?.textContent || undefined,
    }];
  });

  const explicit = read(doc.getElementsByTagName('wpt'));
  if (explicit.length >= 2) return { coordinates: explicit.slice(0, maxPoints), name };

  const track = read(doc.getElementsByTagName('trkpt'));
  if (track.length >= 2) return { coordinates: samplePoints(track, maxPoints), name };

  const routePoints = read(doc.getElementsByTagName('rtept'));
  if (routePoints.length >= 2) return { coordinates: samplePoints(routePoints, maxPoints), name };

  throw new Error('No waypoints or track points found in that GPX file.');
}

export function parseGeoJSON(text: string, maxPoints = 25): ParsedImport {
  let data: any;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('That file is not valid GeoJSON.');
  }
  const features: any[] = data?.type === 'FeatureCollection'
    ? data.features || []
    : data?.type === 'Feature'
      ? [data]
      : [];
  if (features.length === 0) throw new Error('No features found in that GeoJSON.');

  const points = features
    .filter((f) => f?.geometry?.type === 'Point')
    .map((f) => ({
      longitude: Number(f.geometry.coordinates[0]),
      latitude: Number(f.geometry.coordinates[1]),
      label: f.properties?.name || undefined,
      sequence: Number(f.properties?.sequence ?? Number.MAX_SAFE_INTEGER),
    }))
    .filter((p) => Number.isFinite(p.longitude) && Number.isFinite(p.latitude))
    .sort((a, b) => a.sequence - b.sequence)
    .map(({ longitude, latitude, label }) => ({ longitude, latitude, label }));

  if (points.length >= 2) return { coordinates: points.slice(0, maxPoints) };

  const line = features.find((f) => f?.geometry?.type === 'LineString');
  if (line) {
    const coords = (line.geometry.coordinates || [])
      .map((c: number[]) => ({ longitude: Number(c[0]), latitude: Number(c[1]) }))
      .filter((c: any) => Number.isFinite(c.longitude) && Number.isFinite(c.latitude));
    if (coords.length >= 2) {
      return { coordinates: samplePoints(coords, maxPoints), name: line.properties?.name };
    }
  }

  throw new Error('No point or line geometry found in that GeoJSON.');
}

/** Evenly thin a dense track down to the Directions waypoint ceiling. */
export function samplePoints<T>(points: T[], maxPoints: number): T[] {
  if (points.length <= maxPoints) return points;
  const out: T[] = [];
  const stride = (points.length - 1) / (maxPoints - 1);
  for (let i = 0; i < maxPoints; i += 1) {
    out.push(points[Math.round(i * stride)]);
  }
  return out;
}

export function downloadFile(filename: string, contents: string, mimeType: string): void {
  const blob = new Blob([contents], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export function safeFilename(name: string): string {
  return (name || 'route').replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'route';
}
